import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { RefreshCw, ShieldAlert } from "lucide-react";
import { useAuth } from "../../app/AuthContext";
import { api } from "../../shared/api/client";
import type { EnvironmentView, ReleaseReadiness } from "../../shared/types/api";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  GateBadge,
  InlineAlert,
  Select,
} from "../../shared/ui";
import { useReleaseMutation } from "./useReleaseMutation";
import { READINESS_LABELS } from "./releaseVocabulary";

/**
 * §20's readiness panel: named gates, each with a sentence.
 *
 * <p>Deliberately not a percentage. §20's objection is exactly right — "92% ready" tells nobody
 * what to go and do, whereas "QA: Fail — 1 QA-required ticket has failed" does. The reasons are
 * repeated underneath as plain sentences, because that is the list somebody works through.
 *
 * <p>A waived gate still shows Fail, with the waiver beside it (§23). Collapsing the two into a
 * pass would destroy the only evidence that a decision was ever made, and the release would read
 * afterwards as though nothing had gone wrong.
 */
export function ReadinessPanel({
  readiness,
  releaseId,
  targetEnvironmentId,
  fixedToLeg = false,
  onChanged,
}: {
  readiness: ReleaseReadiness;
  releaseId: string;
  /** Where the release is aimed, and what the panel judges it against by default. */
  targetEnvironmentId?: string | null;
  /**
   * True when one leg of the journey is open, in which case the picker is hidden.
   *
   * <p>Two controls that both change which environment the page is about is one too many. Inside
   * a leg the journey has already answered that question, and leaving a dropdown that could
   * disagree with the "Working on Development" badge above it is how somebody reads a production
   * verdict while standing in the sandbox.
   */
  fixedToLeg?: boolean;
  onChanged: () => void;
}) {
  /*
   * §63: "is this ready?" has no answer on its own. A release is ready for staging and not for
   * production, and a single verdict has to pick one of those to be wrong about — so the panel
   * asks which environment, defaulting to the one the release is aimed at.
   */
  const [environmentId, setEnvironmentId] = useState<string>(targetEnvironmentId ?? "");

  const environments = useQuery({
    queryKey: ["release-environments"],
    queryFn: () => api.get<EnvironmentView[]>("/api/v1/environments"),
  });

  const forEnvironment = useQuery({
    queryKey: ["release-readiness-for", releaseId, environmentId],
    queryFn: () =>
      api.get<ReleaseReadiness>(
        `/api/v1/releases/${releaseId}/readiness/${environmentId}`,
      ),
    enabled: environmentId !== "",
  });

  const shown = environmentId === "" ? readiness : forEnvironment.data ?? readiness;
  const chosen = (environments.data ?? []).find((row) => row.id === environmentId) ?? null;
  const { can } = useAuth();
  const { run, saving } = useReleaseMutation(releaseId);
  const waived = shown.gates.filter((gate) => gate.overriddenBy != null);

  /*
   * Every mutation already refreshes the stored verdict, so this is not how the release list
   * stays current — it is the deliberate re-check for when the answer depends on something
   * outside this release, such as a ticket that has since moved on its own board.
   */
  const recheck = () =>
    void run(() => api.post(`/api/v1/releases/${releaseId}/validate`), onChanged);

  /*
   * §21 and §22 are different questions and are shown as such. Execution and post-deployment
   * validation cannot be satisfied before a deployment begins, so listing them beside the start
   * gates made a release in planning look as though its runbook were holding it up.
   */
  const toStart = shown.gates.filter((gate) => !gate.completionGate);
  const toFinish = shown.gates.filter((gate) => gate.completionGate);

  return (
    <Card>
      <CardHeader
        title="Readiness"
        subtitle={
          chosen
            ? `${READINESS_LABELS[shown.status]} — ${chosen.name}`
            : READINESS_LABELS[shown.status]
        }
        action={
          <div className="flex items-center gap-2">
            {!fixedToLeg && (
              <Select
                aria-label="Judge readiness for"
                className="h-8 text-xs"
                value={environmentId}
                onChange={(event) => setEnvironmentId(event.target.value)}
              >
                <option value="">Overall</option>
                {(environments.data ?? []).map((environment) => (
                  <option key={environment.id} value={environment.id}>
                    {environment.name}
                  </option>
                ))}
              </Select>
            )}
            {can("RELEASE_VIEW") && (
            <Button
              size="sm"
              variant="secondary"
              icon={<RefreshCw className="h-4 w-4" />}
              loading={saving}
              onClick={recheck}
            >
              Re-check
            </Button>
            )}
          </div>
        }
      />
      <CardBody className="space-y-3">
        <ul className="divide-y divide-ink-100">
          {toStart.map((gate) => (
            <li key={gate.gate} className="flex items-start justify-between gap-4 py-2">
              <div className="min-w-0">
                <p className="text-sm font-medium text-ink-900">{gate.label}</p>
                {gate.summary && (
                  <p className="mt-0.5 text-xs text-ink-600">{gate.summary}</p>
                )}
                {gate.overriddenBy && (
                  <p className="mt-1 flex items-start gap-1.5 rounded-md bg-violet-50 px-2 py-1 text-xs text-violet-800">
                    <ShieldAlert className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
                    <span>
                      Risk accepted by {gate.overriddenBy}
                      {gate.overrideReason && ` — ${gate.overrideReason}`}
                    </span>
                  </p>
                )}
              </div>
              <div className="shrink-0">
                <GateBadge state={gate.status} waived={gate.overriddenBy != null} />
              </div>
            </li>
          ))}
        </ul>

        {toFinish.length > 0 && (
          <div>
            <p className="mb-1 mt-2 text-xs font-semibold uppercase tracking-wide text-ink-400">
              To finish
            </p>
            <ul className="divide-y divide-ink-100">
              {toFinish.map((gate) => (
                <li key={gate.gate} className="flex items-start justify-between gap-4 py-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-ink-900">{gate.label}</p>
                    {gate.summary && (
                      <p className="mt-0.5 text-xs text-ink-600">{gate.summary}</p>
                    )}
                  </div>
                  <div className="shrink-0">
                    <GateBadge state={gate.status} waived={gate.overriddenBy != null} />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {shown.blockingReasons.length > 0 && (
          <InlineAlert tone="warning">
            <p className="font-medium">
              {shown.blockingReasons.length === 1
                ? "One thing is in the way:"
                : `${shown.blockingReasons.length} things are in the way:`}
            </p>
            <ul className="mt-1 list-inside list-disc space-y-0.5">
              {shown.blockingReasons.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          </InlineAlert>
        )}

        {shown.blockingReasons.length === 0 && waived.length > 0 && (
          <InlineAlert tone="info">
            Nothing is blocking this release, but {waived.length}{" "}
            {waived.length === 1 ? "gate was" : "gates were"} waived rather than passed. It will be
            recorded as released with a waiver.
          </InlineAlert>
        )}
      </CardBody>
    </Card>
  );
}
